import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
  HostListener,
} from '@angular/core';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { BreakpointService } from '../../../services/breakpoint.service';

@Component({
  selector: 'om-dropdown-panel',
  imports: [NgTemplateOutlet],
  templateUrl: './dropdown-panel.html',
  styleUrl: './dropdown-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownPanel {
  readonly isOpen = input<boolean>(false);
  readonly closed = output<void>();

  private readonly bp = inject(BreakpointService);
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  readonly isMobile = computed(() => this.bp.isMobile());
  readonly mobileWrapper = viewChild<ElementRef<HTMLElement>>('mobilePortal');

  readonly panelClass = computed(() => {
    const base =
      'panel-container bg-white dark:bg-slate-900 border-slate-200/60 dark:border-white/10 overflow-hidden flex flex-col';
    if (this.bp.isMobile()) {
      return `${base} fixed bottom-0 left-0 right-0 rounded-t-3xl border-t max-h-[90vh] shadow-[0_-8px_32px_rgba(0,0,0,0.1)]`;
    }
    return `${base} absolute top-full right-0 mt-3 min-w-full rounded-2xl border max-h-[80vh] shadow-[0_8px_32px_rgba(0,0,0,0.12),0_1px_0_rgba(255,255,255,0.8)]`;
  });

  constructor() {
    effect(() => {
      const wrapper = this.mobileWrapper()?.nativeElement;
      if (!wrapper) return;

      if (this.isOpen() && this.bp.isMobile()) {
        this.doc.body.appendChild(wrapper);
      } else if (wrapper.parentElement === this.doc.body) {
        wrapper.remove();
      }
    });

    this.destroyRef.onDestroy(() => {
      const wrapper = this.mobileWrapper()?.nativeElement;
      if (wrapper?.parentElement === this.doc.body) {
        wrapper.remove();
      }
    });
  }

  close(): void {
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) {
      this.close();
    }
  }
}
